import { useEffect, useState } from "react";
import { useLocation, useNavigate, Navigate } from "react-router-dom";

import { useAuth } from "../../providers/auth";
import { useScheduleInfos } from "../../providers/schedule";
import { usePartnerData } from "../../providers/partnerData";

import { api } from "../../lib/api";

import {
  IScheduleDetails,
  IScheduleInfos,
} from "../../providers/schedule/types";
import { IAllDoctorsResponse } from "../../providers/doctorsData/types";

import { IAppointmentData } from "../../types/appointment";

import Toast from "../../utils/toast";

import { ProfessionalPaymentInfosSkeleton } from "../../components/Skeletons/ProfessionalPaymentInfosSkeleton";
import FakeCreditForm from "./components/fakeCreditForm";
import { FakePaymentInfos } from "./components/fakePaymentInfos";
import CreditForm from "../../components/Forms/PaymentForms/CreditForm";
import TicketForm from "../../components/Forms/PaymentForms/TicketForm";
import DebitForm from "../../components/Forms/PaymentForms/DebitForm";
import PixForm from "../../components/Forms/PaymentForms/PixForm";
import Footer from "../../components/Elements/Footer";
import { Header } from "../../components/Elements/Header";
import PaymentOptions from "../../components/Elements/PaymentOptions";
import ProfessionalPaymentInfos from "../../components/Cards/ProfessionalPaymentInfos";
import { storagePrefix } from "../../utils/storage";
import { CircularProgress, Skeleton } from "@mui/material";
import { useAbortableRequest } from "../../hooks/abortableController";

interface IAppointmentState {
  appointmentData: IAppointmentData | undefined;
  scheduleData: IScheduleInfos | undefined;
}

/**
 * Extrai a mensagem real de erro do BadRequest(ModelState) da API
 * (ex.: "Este horário não está mais disponível" / erro de integração ERP).
 */
const extractApiError = (error: any): string | null => {
  const data = error?.response?.data;
  if (!data) return null;

  if (typeof data === "string") return data;

  const errors = data.errors ?? data;
  if (errors && typeof errors === "object") {
    const messages = Object.values(errors)
      .flat()
      .filter((m): m is string => typeof m === "string");
    if (messages.length) return messages.join(" ");
  }

  return null;
};

const PagamentoPage = () => {
  const { token } = useAuth();
  const { partnerUrl } = usePartnerData();

  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);

  const { clearScheduleInfos } = useScheduleInfos();
  const { createController } = useAbortableRequest();

  const navigate = useNavigate();
  const location = useLocation();

  // location.state é null em acesso direto/refresh — sem isso, a desestruturação
  // quebra antes do guard "if (!location.state)" mais abaixo conseguir rodar
  const { appointmentData, scheduleData } = (location.state ?? {}) as Partial<IAppointmentState>;

  const [paymentOption, setPaymentOption] = useState<"credito" | "debito" | "boleto" | "pix">("credito");

  const [scheduleInfos, setScheduleInfos] = useState<IScheduleInfos>(scheduleData ?? {} as IScheduleInfos);

  const localStorageScheduleInfos: IScheduleDetails = JSON.parse(
    localStorage.getItem(`${storagePrefix}scheduleInfo`) || "{}"
  );

  const reset = () => {
    clearScheduleInfos();
    navigate(`/${partnerUrl}`);
    return setLoading(false);
  };

  const handleGetDoctorData = async (idSpecialistDoctor: number) => {
    const abortController = createController(`handleGetDoctorData${idSpecialistDoctor}`);
    try {
      if (!localStorageScheduleInfos || !idSpecialistDoctor) {
        return reset();
      }
      const { data } = (await api.get(`/SpecialistDoctor/list?idSpecialistDoctor=${idSpecialistDoctor}`, {
        signal: abortController.signal
      }
      )) as {
        data: IAllDoctorsResponse;
      };

      const doctor = data.specialistDoctors;

      return doctor.at(0);
    } catch (error) { }
  };

  const handleGetData = async () => {
    try {
      if (!localStorageScheduleInfos) {
        return reset();
      }
      setLoading(true);
      const doctor = await handleGetDoctorData(
        localStorageScheduleInfos.idDoctor
      );

      const place = doctor?.places.find(
        (elm) => elm.id === localStorageScheduleInfos.idPlace
      );

      if (doctor && place) {
        const output = {
          doctor,
          place,
          scheduleDetails: localStorageScheduleInfos.details,
          schedule: localStorageScheduleInfos.schedule,
          isVideo: localStorageScheduleInfos.details.type === "video",
        };

        setScheduleInfos(output);
        setLoading(false);
      }
    } catch (error) {
      console.error(error);
    }
  };


  useEffect(() => {
    if (!scheduleData) {
      handleGetData();
    } else {
      setLoading(false);
    }
  }, [location.state]);

  if (!location.state || !token) {
    return <Navigate to={"/"} />;
  }

  const { isVideo, scheduleDetails, schedule, place } = scheduleInfos;

  console.log(schedule);

  const handleSchedule = async () => {

    setSubmitting(true);
    try {
      const body = {
        // envia o convênio sempre (Particular = 354): a integração ERP do
        // legado quebra com IdConvenio nulo (IntegraAgenda usa .Value direto)
        scheduleTimeId: schedule.agendas[0].idSchedule,
        healthInsuranceId: scheduleDetails.healtyInsurance.id ?? undefined,
        date: schedule.data,
        scheduleType: 0,
      };

      const { data } = await api.post("/Attendance/home", body);

      //* Resultado negativo vem vazio da api, por isso é verificado primeiro.
      if (data) {
        setSubmitting(false);
        return Toast.fire({
          title: "Algo deu errado",
          text: "Por favor, tente novamente mais tarde",
          icon: "warning",
        });

      } else {
        await Toast.fire({
          title: "Consulta agendada",
          text: "Você será redirecionado para outra pagina...",
          icon: "success",
        });
        clearScheduleInfos();
        navigate(`/${partnerUrl}/consultas`);
      }
    } catch (error) {
      console.error(error);

      setSubmitting(false);
      return Toast.fire({
        title: "Não foi possível agendar",
        text: extractApiError(error) ?? "Por favor, tente novamente mais tarde",
        icon: "warning",
      });

    }
  };

  return (
    <>
      <Header />
      <div id="wrapper" className="p-0">
        <div className="page-header flex-fill justify-content-start">
          <div className="shape"></div>
          <div className="container position-relative py-5">
            {loading ? (
              <>
                <div className="d-flex align-items-center flex-column">
                  <Skeleton className="bg-white" height={48} variant="text" width={"30%"} />
                  <Skeleton className="bg-white mb-5" variant="text" width={"60%"} />
                </div>
                <div className="row gy-4">
                  <div className={`${isVideo ? 'col-lg-5 col-xl-4 order-lg-1' : 'col-lg-7 col-xl-8'}`}>
                    <ProfessionalPaymentInfosSkeleton />
                  </div>
                  <div className={`${isVideo ? 'col-lg-7 col-xl-8 order-lg-0' : 'col-lg-5 col-xl-4'}`}>
                    <div className="card text-body bg-light p-3 px-md-5 py-md-4">
                      <FakePaymentInfos />
                      <hr />
                      <FakeCreditForm />
                    </div>
                  </div>
                </div>
              </>
            ) : (
              <>
                {isVideo || appointmentData ? (
                  <>
                    <h1 className="text-center mb-1">Pagamento</h1>
                    <p className="text-center mb-5">
                      {!appointmentData
                        ? "Confirme os dados da teleconsulta e insira os dados de pagamento"
                        : "Efetive o pagamento da sua teleconsulta"}
                    </p>
                  </>
                ) : (
                  <>
                    <h1 className="text-center mb-1">Consulta Presencial</h1>
                    <p className="text-center mb-5">
                      Confirme os Dados da Consulta Presencial
                    </p>
                  </>
                )}
                <div className="row gy-4">
                  <div className={`${isVideo ? 'col-lg-5 col-xl-4 order-lg-1' : 'col-lg-7 col-xl-8'}`}>
                    <ProfessionalPaymentInfos
                      scheduleInfos={scheduleInfos}
                      appointment={appointmentData ? appointmentData : undefined}
                    />
                  </div>
                  <div className={`${isVideo ? 'col-lg-7 col-xl-8 order-lg-0' : 'col-lg-5 col-xl-4'}`}>
                    <div className="card text-body bg-light p-3 px-md-5 py-md-4">
                      {isVideo ? (
                        <>
                          <h5 className="mb-3 center">Forma de Pagamento</h5>
                          <PaymentOptions
                            value={paymentOption}
                            setValue={setPaymentOption}
                          />
                          <hr />
                        </>
                      ) : (
                        <>
                          <h5 className="mb-3 text-center">
                            Antes de finalizar, confira os dados da consulta presencial solicitada no quadro ao lado.
                          </h5>
                          <p className="mb-3 text-center">Se estiver tudo certo, é só confirmar no botão abaixo.</p>

                          <div className="text-center">
                            <button
                              disabled={submitting}
                              className="btn btn-success fw-semibold"
                              onClick={handleSchedule}
                            >
                              {submitting ? (
                                <CircularProgress size={21} color="inherit" />
                              ) : (
                                <>
                                  <i className="las la-check me-2"></i>
                                  Confirmar Consulta Presencial
                                </>
                              )}
                            </button>
                          </div>
                        </>
                      )}

                      {isVideo && (
                        <>
                          {paymentOption === "credito" && (
                            <>
                              <h5 className="mb-3">
                                Dados do cartão de crédito
                              </h5>
                              <div className="d-flex gap-3 align-items-center mb-3">
                                <span className="label">
                                  Cartões Aceitos:
                                </span>
                                <div className="bg-white border rounded p-1">
                                  <img
                                    src={"https://conectadoc.com.br/app-assets/img/imgFormaPagamento.jpg"}
                                    alt="Cartões Aceitos"
                                  />
                                </div>
                              </div>

                              <CreditForm
                                reportMessage={scheduleDetails.reportMessage}
                                healthyInsurance={scheduleDetails.healtyInsurance}
                                schedule={schedule}
                              />
                            </>
                          )}

                          {paymentOption === "debito" && (
                            <>
                              <h5 className="mb-3">
                                Dados do cartão de débito
                              </h5>
                              <div className="d-flex gap-3 align-items-center mb-3">
                                <span className="label">
                                  Cartões/Bancos Aceitos:
                                </span>
                                <div className="bg-white border rounded p-1">
                                  <img
                                    src={"https://conectadoc.com.br/_assets/images/cartoesdebitos.png"}
                                    alt="Cartões Aceitos"
                                  />
                                </div>
                              </div>

                              <DebitForm
                                reportMessage={scheduleDetails.reportMessage}
                                healthyInsurance={scheduleDetails.healtyInsurance}
                                schedule={schedule}
                              />
                            </>
                          )}

                          {paymentOption === "boleto" && (
                            <TicketForm
                              reportMessage={scheduleDetails.reportMessage}
                              healthyInsurance={scheduleDetails.healtyInsurance}
                              schedule={schedule}
                            />
                          )}

                          {paymentOption === "pix" && (
                            <PixForm
                              reportMessage={scheduleDetails.reportMessage}
                              healthyInsurance={scheduleDetails.healtyInsurance}
                              schedule={schedule}
                            />
                          )}
                        </>
                      )}
                    </div>
                  </div>
                </div>
              </>
            )}
          </div>
        </div>
      </div>
      <Footer />
    </>
  );
};

export default PagamentoPage;
